import React from 'react';
import { useCookies } from 'next-client-cookies';
import Link from 'components/commercetools-ui/atoms/link';
import { trackCtaClick } from 'helpers/analytics/ga4Events';
import { fontStyleMap } from 'helpers/constants';
import { Reference } from 'types/reference';
import ArrowTopRightOnSquareIcon from '@heroicons/react/24/solid/ArrowTopRightOnSquareIcon';

export interface FooterLink {
  name: string;
  reference: Reference;
  linkTextColor?: string;
  linkTextSize?: string;
  linkTextStyle?: string;
}

export interface FooterColumn {
  header?: string;
  headerTextColor?: string;
  headerTextSize?: string;
  headerTextStyle?: string;
  links?: FooterLink[];
}
export interface Props {
  column: FooterColumn;
}

const Column: React.FC<Props> = ({ column }) => {
  const { header, links, headerTextColor, headerTextSize, headerTextStyle } = column;
  const cookies = useCookies();
  const locale = (cookies.get('locale') as string) || 'en-us';
  const lang = locale.split(/[-_]/)[0].toLowerCase();
  const headerFontStyle = fontStyleMap[headerTextStyle ?? 'regular'];
  return (
    <div>
      {header && (
        <p
          style={{ color: headerTextColor, fontSize: headerTextSize }}
          data-ffid={`lnk-footer-${header?.trim()?.replace(/[^a-zA-Z0-9]/g, '')}`}
          className={`self-start pb-8 text-14 ${headerFontStyle}`}
        >
          {header}
        </p>
      )}

      {links && links.length > 0 && (
        <ul className={`flex flex-col items-start self-start ${header ? '' : 'lg:pt-25'}`}>
          {links.map((item, i) => {
            const isOpenInNewTab = item?.reference?.openInNewWindow === true;
            const baseRefUrl = typeof item.reference === 'string' ? item.reference : (item.reference?.link ?? '');

            let finalLinkUrl = baseRefUrl;
            if (isOpenInNewTab && baseRefUrl) {
              const trailingSlash = baseRefUrl.endsWith('/') ? '' : '/';
              const langPath = locale?.toLowerCase() === 'en-us' ? `${lang}/` : (locale ?? 'en');
              finalLinkUrl = `${baseRefUrl}${trailingSlash}${langPath}`;
            }

            return (
              <li key={`${item?.name ?? ''}${i}`} className="pb-8">
                <Link
                  variant="footer"
                  data-ffid={`lnk-footer-${item?.name?.trim()?.replace(/[^a-zA-Z0-9]/g, '')}`}
                  data-testid={`footer-link-${item.name}`}
                  link={finalLinkUrl}
                  className={`inline-block w-full ${fontStyleMap[item.linkTextStyle ?? 'regular']}`}
                  style={{
                    color: item?.linkTextColor,
                    fontSize: item?.linkTextSize,
                    fontWeight: fontStyleMap[item?.linkTextStyle ?? 'regular'],
                  }}
                  target={isOpenInNewTab ? '_blank' : undefined}
                  rel={isOpenInNewTab ? 'noopener noreferrer' : undefined}
                  onClick={() => {
                    trackCtaClick('Footer', item.name ?? '', finalLinkUrl);
                  }}
                  prefetch={false}
                >
                    {item.name}
                    {isOpenInNewTab && <span className="sr-only">{'(opens in a new tab)'}</span>}
                    {isOpenInNewTab && <ArrowTopRightOnSquareIcon className="inline ml-2 pb-4 size-20" aria-hidden="true" />}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Column;
